import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Storefront,
  MagnifyingGlass,
  ArrowClockwise,
  Package,
  CheckCircle,
  WarningCircle,
  EyeSlash,
  ImageSquare,
  SquaresFour,
  Rows,
  CaretLeft,
  CaretRight,
  ArrowRight,
  X,
  Code,
  Copy,
  Check,
  PaintBrush,
} from "@phosphor-icons/react";
import { useAppServices } from "../../../hook/services";
import { useAppInfo } from "../../../context/app";
import env from "../../../config";
import ProductDetail from "./ProductDetail";
import Customize, { DEFAULTS } from "./Customize";

const PAGE_SIZE = 24;

// Printful-only storefront, reachable from the "App" tab that
// Location/routes.js adds for appId 68b6bb88fac52f2b120cd9d6. The catalogue
// comes from /apps/action/printful/get-products/:appId, which reads the api key
// and store id saved on this location's install; selecting a product pulls the
// enriched detail (variants, pricing, stock, print files).
const PrintfulApp = () => {
  const { appId, locationId, companyId } = useParams();
  const AppService = useAppServices();
  const [app] = useAppInfo();

  const [products, setProducts] = useState([]);
  const [storeId, setStoreId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("name");
  const [view, setView] = useState("grid");
  const [page, setPage] = useState(1);

  // Embed snippet — keyed on the install document _id, which the widget sends
  // to the public endpoints so the server can resolve the stored credentials.
  const [embedOpen, setEmbedOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [installId, setInstallId] = useState("");

  // Widget customization
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [settings, setSettings] = useState(DEFAULTS);
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    const loadInstall = async () => {
      const { response } = await AppService.app_installs.filter({
        query: `appId=${appId}&locationId=${locationId}`,
      });
      if (response?.data?._id) setInstallId(response.data._id);
      if (response?.data?.credentials?.widget_settings) {
        setSettings({ ...DEFAULTS, ...response.data.credentials.widget_settings });
      }
    };
    loadInstall();
  }, []);

  const saveSettings = async (next) => {
    setSavingSettings(true);
    const { response, error: err } = await AppService.printful.save_widget_settings({
      appId,
      payload: { extras: { locationId, companyId }, data: { settings: next } },
    });
    setSavingSettings(false);

    if (response?.success) {
      setSettings(response.data);
      toast.success("Widget settings saved — live everywhere it's embedded");
      setCustomizeOpen(false);
    } else {
      toast.error(err?.message || "Could not save widget settings.");
    }
  };

  // Always the live API host — a copied snippet is pasted onto real websites,
  // where a localhost src would never resolve.
  const widgetHost = env.API_URL;

  // The store renders wherever this <div> sits; the script appends into it.
  const embedCode = `<div id="printful-store"></div>
<script
  src="${widgetHost}/apps/action/printful/widget.js"
  data-install-id="${installId}"
  data-target="printful-store"
  data-page-size="24"
></script>`;

  const copyEmbed = async () => {
    try {
      await navigator.clipboard.writeText(embedCode);
    } catch (err) {
      // Clipboard API needs a secure context; fall back for plain http.
      const ta = document.createElement("textarea");
      ta.value = embedCode;
      ta.style.position = "fixed";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    toast.success("Embed code copied");
    setTimeout(() => setCopied(false), 2000);
  };

  // Detail slide-over
  const [openProductId, setOpenProductId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");

  const fetchProducts = async (isRefresh) => {
    isRefresh ? setRefreshing(true) : setLoading(true);
    setError("");

    const { response, error: err } = await AppService.printful.get_products({
      appId,
      query: `locationId=${locationId}&companyId=${companyId}`,
    });

    if (response?.success) {
      setProducts(response.data?.products || []);
      setStoreId(response.data?.store_id || null);
      if (isRefresh) toast.success("Store refreshed");
    } else {
      const message = err?.message || "Could not load products from Printful.";
      setError(message);
      if (isRefresh) toast.error(message);
    }

    isRefresh ? setRefreshing(false) : setLoading(false);
  };

  useEffect(() => {
    fetchProducts(false);
  }, []);

  const openProduct = async (productId) => {
    setOpenProductId(productId);
    setDetail(null);
    setDetailError("");
    setDetailLoading(true);

    const { response, error: err } = await AppService.printful.get_product({
      appId,
      query: `locationId=${locationId}&companyId=${companyId}&productId=${productId}`,
    });

    if (response?.success) setDetail(response.data);
    else setDetailError(err?.message || "Could not load this product from Printful.");

    setDetailLoading(false);
  };

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = products;

    if (q) {
      list = list.filter(
        (p) =>
          p.name?.toLowerCase().includes(q) ||
          String(p.id).includes(q) ||
          p.external_id?.toLowerCase().includes(q)
      );
    }

    const sorted = [...list];
    if (sort === "name") sorted.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    if (sort === "name_desc") sorted.sort((a, b) => (b.name || "").localeCompare(a.name || ""));
    if (sort === "variants") sorted.sort((a, b) => (b.variants || 0) - (a.variants || 0));
    return sorted;
  }, [products, search, sort]);

  useEffect(() => {
    setPage(1);
  }, [search, sort, view]);

  const totalPages = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = visible.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const stats = useMemo(() => {
    const variants = products.reduce((sum, p) => sum + (p.variants || 0), 0);
    const synced = products.reduce((sum, p) => sum + (p.synced || 0), 0);
    const ignored = products.filter((p) => p.is_ignored).length;
    return { variants, synced, ignored };
  }, [products]);

  const goToPage = (next) => {
    setPage(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Design tokens: premium ink + gold, Rubik headings / Nunito Sans body.
  const styles = `
    @import url('https://fonts.googleapis.com/css2?family=Nunito+Sans:wght@400;500;600;700&family=Rubik:wght@500;600;700&display=swap');

    .pf-shell {
      --pf-ink: #1C1917;
      --pf-ink-2: #44403C;
      --pf-gold: #CA8A04;
      --pf-bg: #FAFAF9;
      --pf-text: #0C0A09;
      --pf-muted: #57534E;
      --pf-line: #E7E5E4;
      background: var(--pf-bg);
      color: var(--pf-text);
      font-family: 'Nunito Sans', system-ui, sans-serif;
      min-height: 100vh;
    }
    .pf-shell h1, .pf-shell h2, .pf-shell h3, .pf-shell h4, .pf-shell h5, .pf-shell .pf-display {
      font-family: 'Rubik', system-ui, sans-serif; letter-spacing: -.01em;
    }

    .pf-hero {
      background:
        radial-gradient(900px 300px at 88% -40%, rgba(202,138,4,.28), transparent 60%),
        linear-gradient(135deg, #1C1917 0%, #292524 60%, #1C1917 100%);
      color: #fff; border-radius: 1.25rem;
    }
    .pf-kpi {
      background: rgba(255,255,255,.07);
      border: 1px solid rgba(255,255,255,.14);
      border-radius: .9rem; min-width: 104px;
    }
    .pf-kpi-value { font-family: 'Rubik', sans-serif; font-size: 1.35rem; line-height: 1.1; }
    .pf-kpi-label { font-size: .72rem; letter-spacing: .04em; text-transform: uppercase; opacity: .72; }

    .pf-toolbar {
      position: sticky; top: 0; z-index: 20;
      background: rgba(250,250,249,.92);
      border: 1px solid var(--pf-line); border-radius: 1rem;
      backdrop-filter: saturate(140%) blur(6px);
    }
    .pf-input { border-color: var(--pf-line); font-size: .9rem; }
    .pf-input:focus, .pf-select:focus {
      border-color: var(--pf-gold); box-shadow: 0 0 0 .2rem rgba(202,138,4,.18);
    }
    .pf-shell .btn:focus-visible, .pf-shell [role="button"]:focus-visible, .pf-shell select:focus-visible {
      outline: 2px solid var(--pf-gold); outline-offset: 2px;
    }
    .pf-btn-ink {
      background: var(--pf-ink); color: #fff; border: 1px solid var(--pf-ink);
      transition: background .2s ease, border-color .2s ease;
    }
    .pf-btn-ink:hover:not(:disabled) { background: #000; color: #fff; }
    .pf-btn-ink:disabled { opacity: .55; }
    .pf-btn-gold {
      background: var(--pf-gold); color: #1C1917; border: 1px solid var(--pf-gold); font-weight: 700;
      transition: filter .2s ease;
    }
    .pf-btn-gold:hover { filter: brightness(1.08); color: #1C1917; }
    .pf-embed-backdrop { position: fixed; inset: 0; background: rgba(12,10,9,.55); z-index: 1090; }
    .pf-embed {
      position: fixed; top: 50%; left: 50%; transform: translate(-50%,-50%);
      width: min(720px, 94vw); background: #fff; border-radius: 1rem; z-index: 1095;
      box-shadow: 0 28px 70px rgba(12,10,9,.32); overflow: hidden;
    }
    .pf-code {
      background: #1C1917; color: #E7E5E4; border-radius: .75rem; padding: 1rem;
      font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: .78rem;
      line-height: 1.6; white-space: pre-wrap; word-break: break-all; margin: 0;
    }
    .pf-seg .btn { border-color: var(--pf-line); color: var(--pf-muted); }
    .pf-seg .btn.active { background: var(--pf-ink); border-color: var(--pf-ink); color: #fff; }

    .pf-card {
      position: relative; display: flex; flex-direction: column; height: 100%;
      background: #fff; border: 1px solid var(--pf-line); border-radius: 1rem;
      overflow: hidden; cursor: pointer; text-align: left; width: 100%; padding: 0;
      transition: box-shadow .2s ease, border-color .2s ease, transform .2s ease;
    }
    .pf-card:hover { border-color: rgba(202,138,4,.55); box-shadow: 0 12px 30px rgba(28,25,23,.12); transform: translateY(-3px); }
    .pf-thumb {
      position: relative; aspect-ratio: 1/1; background: #F5F5F4;
      display: flex; align-items: center; justify-content: center; overflow: hidden;
    }
    .pf-thumb img { width: 100%; height: 100%; object-fit: contain; max-width: 100%; transition: transform .3s ease; }
    .pf-card:hover .pf-thumb img { transform: scale(1.05); }
    .pf-view {
      position: absolute; left: .6rem; right: .6rem; bottom: .6rem;
      background: var(--pf-ink); color: #fff; border-radius: .6rem;
      font-size: .8rem; font-weight: 600; padding: .5rem .7rem;
      display: flex; align-items: center; justify-content: center; gap: .35rem;
      opacity: 0; transform: translateY(6px); transition: opacity .2s ease, transform .2s ease;
    }
    .pf-card:hover .pf-view, .pf-card:focus-visible .pf-view { opacity: 1; transform: none; }
    .pf-name {
      font-weight: 600; font-size: .92rem; line-height: 1.4; color: var(--pf-text);
      display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
    }

    .pf-chip { font-size: .72rem; font-weight: 600; padding: .2rem .55rem; border-radius: 999px; }
    .pf-chip-ok { background: #ECFDF5; color: #047857; }
    .pf-chip-warn { background: #FEF7E6; color: #A16207; }
    .pf-chip-mute { background: #F5F5F4; color: var(--pf-muted); }

    .pf-row {
      display: flex; align-items: center; gap: 1rem; width: 100%; text-align: left;
      background: #fff; border: 1px solid var(--pf-line); border-radius: .9rem;
      padding: .65rem; cursor: pointer;
      transition: border-color .2s ease, box-shadow .2s ease;
    }
    .pf-row:hover { border-color: rgba(202,138,4,.55); box-shadow: 0 6px 18px rgba(28,25,23,.08); }
    .pf-row-thumb { width: 62px; height: 62px; border-radius: .6rem; object-fit: contain; background: #F5F5F4; flex-shrink: 0; }

    .pf-skeleton {
      background: linear-gradient(90deg,#EFEDEB 25%,#F7F6F5 37%,#EFEDEB 63%);
      background-size: 400% 100%; animation: pf-shimmer 1.4s ease-in-out infinite; border-radius: 1rem;
    }
    @keyframes pf-shimmer { 0% { background-position: 100% 50% } 100% { background-position: 0 50% } }
    .pf-spin { animation: pf-rotate 1s linear infinite; }
    @keyframes pf-rotate { to { transform: rotate(360deg) } }

    @media (prefers-reduced-motion: reduce) {
      .pf-shell *, .pf-shell *::before, .pf-shell *::after {
        animation-duration: .001ms !important; animation-iteration-count: 1 !important;
        transition-duration: .001ms !important;
      }
      .pf-card:hover { transform: none; }
    }
  `;

  const Kpi = ({ label, value }) => (
    <div className="pf-kpi px-3 py-2 text-center">
      <div className="pf-kpi-value fw-bold">{value}</div>
      <div className="pf-kpi-label">{label}</div>
    </div>
  );

  const Thumb = ({ url, alt, className }) =>
    url ? (
      <img src={url} alt={alt} loading="lazy" decoding="async" className={className} />
    ) : (
      <div className="d-flex align-items-center justify-content-center w-100 h-100" style={{ color: "#A8A29E" }}>
        <ImageSquare size={30} weight="thin" aria-hidden="true" />
      </div>
    );

  const SyncChips = ({ p }) => (
    <>
      <span className="pf-chip pf-chip-mute">
        {p.variants} variant{p.variants === 1 ? "" : "s"}
      </span>
      {p.synced === p.variants ? (
        <span className="pf-chip pf-chip-ok d-inline-flex align-items-center gap-1">
          <CheckCircle size={11} weight="fill" aria-hidden="true" /> Synced
        </span>
      ) : (
        <span className="pf-chip pf-chip-warn">{p.synced}/{p.variants} synced</span>
      )}
      {p.is_ignored && (
        <span className="pf-chip pf-chip-mute d-inline-flex align-items-center gap-1">
          <EyeSlash size={11} weight="bold" aria-hidden="true" /> Ignored
        </span>
      )}
    </>
  );

  return (
    <div className="pf-shell py-4 py-lg-5">
      <style>{styles}</style>
      <div className="container-fluid px-3 px-lg-5" style={{ maxWidth: 1500 }}>
        {/* Hero */}
        <div className="pf-hero p-4 p-lg-4 mb-3">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
            <div className="d-flex align-items-center gap-3">
              <div
                className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
                style={{ width: 54, height: 54, background: "linear-gradient(135deg,#CA8A04,#A16207)" }}
              >
                <Storefront size={26} weight="bold" aria-hidden="true" />
              </div>
              <div>
                <h4 className="fw-bold mb-1">{app?.appName || "Printful"} Store</h4>
                <div style={{ fontSize: ".85rem", opacity: 0.78 }}>
                  {storeId ? `Store ${storeId}` : "Your synced product catalogue"}
                </div>
              </div>
            </div>

            <div className="d-flex align-items-center gap-2 flex-wrap">
              <Kpi label="Products" value={products.length} />
              <Kpi label="Variants" value={stats.variants} />
              <Kpi label="Synced" value={stats.synced} />
              <Kpi label="Ignored" value={stats.ignored} />

              <button
                type="button"
                className="btn btn-sm rounded-3 d-flex align-items-center gap-2 ms-lg-2"
                onClick={() => setCustomizeOpen(true)}
                style={{
                  minHeight: 44,
                  background: "rgba(255,255,255,.1)",
                  border: "1px solid rgba(255,255,255,.25)",
                  color: "#fff",
                  fontWeight: 600,
                }}
              >
                <PaintBrush size={16} weight="bold" aria-hidden="true" />
                Customize
              </button>

              <button
                type="button"
                className="btn btn-sm pf-btn-gold rounded-3 d-flex align-items-center gap-2"
                onClick={() => setEmbedOpen(true)}
                disabled={!installId}
                title={!installId ? "Loading install…" : "Copy the embed code"}
                style={{ minHeight: 44 }}
              >
                <Code size={16} weight="bold" aria-hidden="true" />
                Copy embed code
              </button>
            </div>
          </div>
        </div>

        {/* Toolbar */}
        <div className="pf-toolbar p-2 p-lg-3 mb-4 d-flex flex-wrap align-items-center gap-2">
          <div className="flex-grow-1" style={{ minWidth: 200, maxWidth: 380 }}>
            <label htmlFor="pf-search" className="visually-hidden">Search products</label>
            <div className="input-group">
              <span className="input-group-text bg-white border-end-0 pf-input" style={{ color: "#78716C" }}>
                <MagnifyingGlass size={16} weight="bold" aria-hidden="true" />
              </span>
              <input
                id="pf-search"
                type="search"
                className="form-control border-start-0 ps-0 pf-input"
                placeholder="Search products…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && (
                <button
                  type="button"
                  className="btn btn-outline-secondary border-start-0 pf-input"
                  onClick={() => setSearch("")}
                  aria-label="Clear search"
                >
                  <X size={14} weight="bold" aria-hidden="true" />
                </button>
              )}
            </div>
          </div>

          <label htmlFor="pf-sort" className="visually-hidden">Sort products</label>
          <select
            id="pf-sort"
            className="form-select form-select-sm rounded-3 pf-select pf-input"
            style={{ width: "auto", minHeight: 38 }}
            value={sort}
            onChange={(e) => setSort(e.target.value)}
          >
            <option value="name">Name A–Z</option>
            <option value="name_desc">Name Z–A</option>
            <option value="variants">Most variants</option>
          </select>

          <div className="ms-auto d-flex align-items-center gap-2">
            <span className="d-none d-md-inline" style={{ fontSize: ".82rem", color: "#57534E" }}>
              {visible.length} of {products.length}
            </span>

            <div className="btn-group pf-seg btn-group-sm" role="group" aria-label="View mode">
              <button
                type="button"
                className={`btn ${view === "grid" ? "active" : ""}`}
                onClick={() => setView("grid")}
                aria-pressed={view === "grid"}
                aria-label="Grid view"
                style={{ minWidth: 44, minHeight: 38 }}
              >
                <SquaresFour size={16} weight="bold" aria-hidden="true" />
              </button>
              <button
                type="button"
                className={`btn ${view === "list" ? "active" : ""}`}
                onClick={() => setView("list")}
                aria-pressed={view === "list"}
                aria-label="List view"
                style={{ minWidth: 44, minHeight: 38 }}
              >
                <Rows size={16} weight="bold" aria-hidden="true" />
              </button>
            </div>

            <button
              type="button"
              className="btn btn-sm pf-btn-ink rounded-3 d-flex align-items-center gap-2"
              onClick={() => fetchProducts(true)}
              disabled={refreshing || loading}
              style={{ minHeight: 38 }}
            >
              <ArrowClockwise
                size={15}
                weight="bold"
                className={refreshing ? "pf-spin" : ""}
                aria-hidden="true"
              />
              {refreshing ? "Refreshing…" : "Refresh"}
            </button>
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="row g-3 g-lg-4" aria-busy="true" aria-live="polite">
            {Array.from({ length: 8 }).map((_, i) => (
              <div className="col-6 col-md-4 col-xl-3" key={i}>
                <div className="pf-skeleton" style={{ height: 320 }} />
              </div>
            ))}
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div className="bg-white border rounded-4 text-center py-5 px-3" style={{ borderColor: "#E7E5E4" }}>
            <WarningCircle size={40} weight="fill" className="mb-3" style={{ color: "#B91C1C" }} aria-hidden="true" />
            <h6 className="fw-bold mb-1">Couldn't load products</h6>
            <p className="mb-3" style={{ fontSize: ".88rem", color: "#57534E" }}>{error}</p>
            <button className="btn btn-sm pf-btn-ink rounded-3" onClick={() => fetchProducts(true)}>
              Try again
            </button>
          </div>
        )}

        {/* Empty */}
        {!loading && !error && visible.length === 0 && (
          <div className="bg-white border rounded-4 text-center py-5 px-3" style={{ borderColor: "#E7E5E4" }}>
            <Package size={40} weight="thin" className="mb-3" style={{ color: "#A8A29E" }} aria-hidden="true" />
            <h6 className="fw-bold mb-1">
              {products.length === 0 ? "No products in this store" : "No matches"}
            </h6>
            <p className="mb-0" style={{ fontSize: ".88rem", color: "#57534E" }}>
              {products.length === 0
                ? "Add products in Printful and they'll show up here."
                : "Try a different search."}
            </p>
          </div>
        )}

        {/* Grid */}
        {!loading && !error && pageItems.length > 0 && view === "grid" && (
          <div className="row g-3 g-lg-4">
            {pageItems.map((p) => (
              <div className="col-6 col-md-4 col-xl-3" key={p.id}>
                <button type="button" className="pf-card" onClick={() => openProduct(p.id)}>
                  <div className="pf-thumb">
                    <Thumb url={p.thumbnail_url} alt={p.name} />
                    <span className="pf-view">
                      View product <ArrowRight size={14} weight="bold" aria-hidden="true" />
                    </span>
                  </div>
                  <div className="p-3 d-flex flex-column flex-grow-1">
                    <div className="pf-name mb-2" title={p.name}>{p.name}</div>
                    <div className="d-flex flex-wrap gap-1 mt-auto">
                      <SyncChips p={p} />
                    </div>
                  </div>
                </button>
              </div>
            ))}
          </div>
        )}

        {/* List */}
        {!loading && !error && pageItems.length > 0 && view === "list" && (
          <div className="d-flex flex-column gap-2">
            {pageItems.map((p) => (
              <button type="button" className="pf-row" key={p.id} onClick={() => openProduct(p.id)}>
                <span className="pf-row-thumb d-flex align-items-center justify-content-center overflow-hidden">
                  <Thumb url={p.thumbnail_url} alt={p.name} className="w-100 h-100" />
                </span>
                <span className="flex-grow-1 min-width-0">
                  <span className="d-block fw-semibold text-truncate" style={{ fontSize: ".93rem" }}>
                    {p.name}
                  </span>
                </span>
                <span className="d-flex align-items-center gap-1 flex-shrink-0">
                  <SyncChips p={p} />
                </span>
              </button>
            ))}
          </div>
        )}

        {/* Pagination */}
        {!loading && !error && visible.length > PAGE_SIZE && (
          <nav className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-4" aria-label="Product pages">
            <span style={{ fontSize: ".82rem", color: "#57534E" }}>
              Showing {(currentPage - 1) * PAGE_SIZE + 1}–
              {Math.min(currentPage * PAGE_SIZE, visible.length)} of {visible.length}
            </span>

            <div className="d-flex align-items-center gap-2">
              <button
                className="btn btn-sm btn-outline-dark rounded-3 d-flex align-items-center gap-1"
                disabled={currentPage === 1}
                onClick={() => goToPage(currentPage - 1)}
                style={{ minHeight: 38 }}
              >
                <CaretLeft size={14} weight="bold" aria-hidden="true" /> Prev
              </button>
              <span style={{ fontSize: ".85rem", fontWeight: 600 }}>
                Page {currentPage} of {totalPages}
              </span>
              <button
                className="btn btn-sm btn-outline-dark rounded-3 d-flex align-items-center gap-1"
                disabled={currentPage === totalPages}
                onClick={() => goToPage(currentPage + 1)}
                style={{ minHeight: 38 }}
              >
                Next <CaretRight size={14} weight="bold" aria-hidden="true" />
              </button>
            </div>
          </nav>
        )}
      </div>

      {customizeOpen && (
        <Customize
          initial={settings}
          saving={savingSettings}
          onSave={saveSettings}
          onClose={() => setCustomizeOpen(false)}
        />
      )}

      {embedOpen && (
        <>
          <div className="pf-embed-backdrop" onClick={() => setEmbedOpen(false)} aria-hidden="true" />
          <div className="pf-embed" role="dialog" aria-modal="true" aria-label="Embed this store">
            <div className="d-flex align-items-start gap-3 p-3 p-lg-4 border-bottom" style={{ borderColor: "#E7E5E4" }}>
              <div
                className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
                style={{ width: 44, height: 44, background: "#FEF7E6", color: "#A16207" }}
              >
                <Code size={20} weight="bold" aria-hidden="true" />
              </div>
              <div className="flex-grow-1">
                <h5 className="fw-bold mb-1" style={{ fontSize: "1.05rem" }}>Embed this store</h5>
                <div style={{ fontSize: ".85rem", color: "#57534E" }}>
                  Paste this into any HTML page. The store renders inside the div.
                </div>
              </div>
              <button
                type="button"
                className="btn btn-sm btn-light rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                style={{ width: 36, height: 36 }}
                onClick={() => setEmbedOpen(false)}
                aria-label="Close"
              >
                <X size={16} weight="bold" aria-hidden="true" />
              </button>
            </div>

            <div className="p-3 p-lg-4">
              <pre className="pf-code">{embedCode}</pre>

              <div className="d-flex flex-wrap align-items-center gap-2 mt-3">
                <button
                  type="button"
                  className={`btn btn-sm rounded-3 d-flex align-items-center gap-2 ${copied ? "btn-success" : "pf-btn-ink"}`}
                  onClick={copyEmbed}
                  style={{ minHeight: 42 }}
                >
                  {copied ? <Check size={15} weight="bold" /> : <Copy size={15} weight="bold" />}
                  {copied ? "Copied!" : "Copy code"}
                </button>
                <span style={{ fontSize: ".8rem", color: "#57534E" }}>
                  Works on any site — no framework needed.
                </span>
              </div>
            </div>
          </div>
        </>
      )}

      {openProductId && (
        <ProductDetail
          data={detail}
          loading={detailLoading}
          error={detailError}
          onClose={() => setOpenProductId(null)}
          onRetry={() => openProduct(openProductId)}
        />
      )}
    </div>
  );
};

export default PrintfulApp;
